import * as React from 'react';
import propz from 'propz';
import { History, Location } from 'history';
import { parse } from 'query-string';
import { Loader } from 'Src/components/Loader/Loader';
import {
  getSuperAdminPermissionRequest,
  SuperAdminPermissionRequest as SuperAdminPermissionRequestModel,
  updateSuperAdminPermissionRequestStatus
} from 'Src/helpers/service/superadmin/permissionRequests';
import { getAllSuperAdminSports, SuperAdminSport } from 'Src/helpers/service/superadmin/sports';
import { getAllSuperAdminSchoolForms } from 'Src/helpers/service/superadmin/forms';
import { getAllSuperAdminSchoolHouses } from 'Src/helpers/service/superadmin/houses';
import { getAllSuperAdminSchoolStudents, mergeSuperAdminSchoolStudent } from 'Src/helpers/service/superadmin/students';
import { getAllSuperAdminUserPermissions, SuperAdminUserPermission } from 'Src/helpers/service/superadmin/users';

interface Props {
  history: History;
  location: Location;
  match: {
    params: {
      requestId: string;
    };
  };
}

interface State {
  item: SuperAdminPermissionRequestModel | null;
  sports: SuperAdminSport[];
  selectedSportIds: string[];
  sportSearchText: string;
  isSportDropdownOpen: boolean;
  existingPermissions: SuperAdminUserPermission[];
  isExistingPermissionsLoading: boolean;
  forms: any[];
  houses: any[];
  students: any[];
  selectedFormId: string;
  selectedHouseId: string;
  selectedMergeStudentId: string;
  studentSearchText: string;
  isStudentDropdownOpen: boolean;
  selectedParentFormId: string;
  selectedParentHouseId: string;
  selectedParentStudentId: string;
  parentStudentSearchText: string;
  isParentStudentDropdownOpen: boolean;
  isSchoolDataLoading: boolean;
  isLoading: boolean;
  isSaving: boolean;
  errorMessage: string;
  successMessage: string;
}

function formatDateTime(value?: string): string {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  return isNaN(date.getTime()) ? value : date.toLocaleString('en-GB');
}

function formatDate(value?: string): string {
  return formatDateTime(value).split(',')[0];
}

function getRequesterName(item: SuperAdminPermissionRequestModel): string {
  const firstName = propz.get(item, ['requester', 'firstName'], '');
  const lastName = propz.get(item, ['requester', 'lastName'], '');

  return `${firstName} ${lastName}`.trim();
}

function getSchoolName(item: SuperAdminPermissionRequestModel): string {
  return propz.get(item, ['requestedPermission', 'school', 'name'], '');
}

function getSchoolId(item: SuperAdminPermissionRequestModel): string {
  return propz.get(item, ['requestedPermission', 'schoolId'], '');
}

function getRequesterId(item: SuperAdminPermissionRequestModel): string {
  return item.requesterId || propz.get(item, ['requester', 'id'], '');
}

function getRole(item: SuperAdminPermissionRequestModel): string {
  return propz.get(item, ['requestedPermission', 'preset'], '');
}

function getSportsText(item: SuperAdminPermissionRequestModel): string {
  const sports = item.sports || [];

  return sports
    .map(sport => sport.name)
    .filter(name => typeof name !== 'undefined' && name !== '')
    .join(', ');
}

const COMPLEX_ACCEPT_PRESETS = ['PARENT'];
const SPORT_ACCEPT_PRESETS = ['TEACHER', 'COACH'];
const STUDENT_ACCEPT_PRESETS = ['STUDENT'];

function isSimpleAcceptPreset(item: SuperAdminPermissionRequestModel): boolean {
  const preset = getRole(item);

  return (
    preset !== '' &&
    COMPLEX_ACCEPT_PRESETS.indexOf(preset) === -1 &&
    SPORT_ACCEPT_PRESETS.indexOf(preset) === -1 &&
    STUDENT_ACCEPT_PRESETS.indexOf(preset) === -1
  );
}

function isSportAcceptPreset(item: SuperAdminPermissionRequestModel): boolean {
  const preset = getRole(item);

  return SPORT_ACCEPT_PRESETS.indexOf(preset) !== -1;
}

function isStudentAcceptPreset(item: SuperAdminPermissionRequestModel): boolean {
  const preset = getRole(item);

  return STUDENT_ACCEPT_PRESETS.indexOf(preset) !== -1;
}

function isParentAcceptPreset(item: SuperAdminPermissionRequestModel): boolean {
  const preset = getRole(item);

  return preset === 'PARENT';
}

function getEntityId(entity?: any, fallback?: string): string {
  return entity && (entity.id || entity._id) ? String(entity.id || entity._id) : fallback || '';
}

function getStudentId(student?: any): string {
  return getEntityId(student);
}

function getStudentName(student?: any): string {
  if (!student) {
    return '';
  }

  const firstName = student.firstName || propz.get(student, ['user', 'firstName'], '');
  const lastName = student.lastName || propz.get(student, ['user', 'lastName'], '');

  return `${firstName} ${lastName}`.trim() || getStudentId(student);
}

function getStudentFormId(student?: any): string {
  return (
    propz.get(student, ['form', 'id'], '') ||
    propz.get(student, ['form', '_id'], '') ||
    student.formId ||
    student.form_id ||
    ''
  );
}

function getStudentHouseId(student?: any): string {
  return (
    propz.get(student, ['house', 'id'], '') ||
    propz.get(student, ['house', '_id'], '') ||
    student.houseId ||
    student.house_id ||
    ''
  );
}

function getStudentSearchText(student?: any): string {
  if (!student) {
    return '';
  }

  return [
    getStudentName(student),
    student.email,
    student.misEmail,
    student.misId,
    propz.get(student, ['form', 'name'], ''),
    propz.get(student, ['house', 'name'], ''),
    formatDate(student.birthday)
  ]
    .filter(value => typeof value !== 'undefined' && value !== null && String(value).trim() !== '')
    .join(' ')
    .toLowerCase();
}

function getStudentDescription(student?: any): string {
  if (!student) {
    return '';
  }

  return [
    formatDate(student.birthday),
    propz.get(student, ['form', 'name'], ''),
    propz.get(student, ['house', 'name'], ''),
    student.email || student.misEmail || '',
    student.misId ? `MIS ID: ${student.misId}` : ''
  ]
    .filter(value => typeof value !== 'undefined' && value !== null && String(value).trim() !== '')
    .join(' · ');
}

function stringifyValue(value: any): string {
  if (typeof value === 'undefined' || value === null) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return String(value);
  }
}

export class SuperAdminPermissionRequest extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      item: null,
      sports: [],
      selectedSportIds: [],
      sportSearchText: '',
      isSportDropdownOpen: false,
      existingPermissions: [],
      isExistingPermissionsLoading: false,
      forms: [],
      houses: [],
      students: [],
      selectedFormId: '',
      selectedHouseId: '',
      selectedMergeStudentId: '',
      studentSearchText: '',
      isStudentDropdownOpen: false,
      selectedParentFormId: '',
      selectedParentHouseId: '',
      selectedParentStudentId: '',
      parentStudentSearchText: '',
      isParentStudentDropdownOpen: false,
      isSchoolDataLoading: false,
      isLoading: false,
      isSaving: false,
      errorMessage: '',
      successMessage: ''
    };
  }

  componentDidMount() {
    this.loadRequest();
    this.loadSports();
  }

  getRequestId(): string {
    return this.props.match.params.requestId;
  }

  loadRequest = () => {
    this.setState({
      isLoading: true,
      errorMessage: '',
      successMessage: ''
    });

    getSuperAdminPermissionRequest(this.getRequestId())
      .then(item => {
        this.setState(
          {
            item,
            selectedSportIds: Array.isArray(item.sportIds) ? item.sportIds : [],
            selectedFormId: propz.get(item, ['requestedPermission', 'childFormId'], '') || '',
            selectedHouseId: propz.get(item, ['requestedPermission', 'childHouseId'], '') || '',
            selectedMergeStudentId: '',
            studentSearchText: '',
            isStudentDropdownOpen: false,
            selectedParentFormId: '',
            selectedParentHouseId: '',
            selectedParentStudentId: '',
            parentStudentSearchText: '',
            isParentStudentDropdownOpen: false,
            isLoading: false,
            errorMessage: ''
          },
          () => {
            this.loadExistingRequesterPermissions(item);
            if (isStudentAcceptPreset(item) || isParentAcceptPreset(item)) {
              this.loadSchoolAcceptData(item);
            }
          }
        );
      })
      .catch(error => {
        console.error('Can not load permission request', error);
        this.setState({
          isLoading: false,
          errorMessage: 'Can not load permission request. Please check the session and try again.'
        });
      });
  };

  loadSports = () => {
    getAllSuperAdminSports()
      .then(sports => {
        this.setState({ sports });
      })
      .catch(error => {
        console.error('Can not load sports', error);
        this.setState({
          errorMessage: 'Can not load sports list. Teacher/Coach accept flow may be incomplete.'
        });
      });
  };

  loadExistingRequesterPermissions = (item: SuperAdminPermissionRequestModel) => {
    const requesterId = getRequesterId(item);

    if (!requesterId) {
      this.setState({
        existingPermissions: [],
        isExistingPermissionsLoading: false
      });
      return;
    }

    this.setState({
      isExistingPermissionsLoading: true
    });

    getAllSuperAdminUserPermissions(requesterId)
      .then(existingPermissions => {
        this.setState({
          existingPermissions,
          isExistingPermissionsLoading: false
        });
      })
      .catch(error => {
        console.error('Can not load requester permissions', error);
        this.setState({
          existingPermissions: [],
          isExistingPermissionsLoading: false,
          errorMessage: 'Can not load existing requester permissions.'
        });
      });
  };

  loadSchoolAcceptData = (item: SuperAdminPermissionRequestModel) => {
    const schoolId = getSchoolId(item);

    if (!schoolId) {
      this.setState({
        errorMessage: 'Can not load school accept data because school is not set on this request.'
      });
      return;
    }

    this.setState({
      isSchoolDataLoading: true
    });

    Promise.all([
      getAllSuperAdminSchoolForms(schoolId),
      getAllSuperAdminSchoolHouses(schoolId),
      getAllSuperAdminSchoolStudents(schoolId)
    ])
      .then(([forms, houses, students]) => {
        this.setState({
          forms,
          houses,
          students,
          isSchoolDataLoading: false
        });
      })
      .catch(error => {
        console.error('Can not load school accept data', error);
        this.setState({
          isSchoolDataLoading: false,
          errorMessage:
            'Can not load forms, houses or students for this school. Student/Parent accept flow may be incomplete.'
        });
      });
  };

  onBackClick = () => {
    const search = parse(this.props.location.search);
    const returnTo = typeof search.returnTo === 'string' ? search.returnTo : '';

    if (returnTo.indexOf('/permission-requests') === 0) {
      this.props.history.push(returnTo);
      return;
    }

    this.props.history.push('/permission-requests');
  };

  onOpenRequesterClick = () => {
    const { item } = this.state;

    if (!item) {
      return;
    }

    const requesterId = getRequesterId(item);

    if (!requesterId) {
      return;
    }

    this.props.history.push(`/users/${requesterId}`);
  };

  onOpenSchoolClick = () => {
    const { item } = this.state;

    if (!item) {
      return;
    }

    const schoolId = getSchoolId(item);

    if (!schoolId) {
      return;
    }

    this.props.history.push(`/schools/${schoolId}`);
  };

  canReject(): boolean {
    const { item } = this.state;

    return !!item && item.status === 'NEW';
  }

  canAccept(): boolean {
    const { item } = this.state;

    return !!item && item.status === 'NEW' && (isSimpleAcceptPreset(item) || isSportAcceptPreset(item));
  }

  canAcceptStudentAsNew(): boolean {
    const { item } = this.state;

    return !!item && item.status === 'NEW' && isStudentAcceptPreset(item);
  }

  canMergeStudent(): boolean {
    const { item, selectedMergeStudentId } = this.state;

    return !!item && item.status === 'NEW' && isStudentAcceptPreset(item) && selectedMergeStudentId !== '';
  }

  canAcceptParentForSelectedStudent(): boolean {
    const { item, selectedParentStudentId } = this.state;

    return !!item && item.status === 'NEW' && isParentAcceptPreset(item) && selectedParentStudentId !== '';
  }

  onRejectClick = () => {
    const { item } = this.state;

    if (!item || !this.canReject()) {
      return;
    }

    const requesterName = getRequesterName(item) || 'this permission request';
    const isConfirmed = window.confirm(`Do you really want to reject ${requesterName}?`);

    if (!isConfirmed) {
      return;
    }

    this.setState({
      isSaving: true,
      errorMessage: '',
      successMessage: ''
    });

    updateSuperAdminPermissionRequestStatus(item.id, {
      status: 'REJECTED'
    })
      .then(updatedItem => {
        this.setState({
          item: updatedItem,
          isSaving: false,
          successMessage: 'Permission request has been rejected.'
        });
      })
      .catch(error => {
        console.error('Can not reject permission request', error);
        this.setState({
          isSaving: false,
          errorMessage: 'Can not reject permission request. Please try again.'
        });
      });
  };

  onAcceptClick = () => {
    const { item } = this.state;

    if (!item || !this.canAccept()) {
      return;
    }

    const requesterName = getRequesterName(item) || 'this permission request';
    const role = getRole(item);
    const isConfirmed = window.confirm(`Do you really want to accept ${requesterName} as ${role}?`);

    if (!isConfirmed) {
      return;
    }

    this.setState({
      isSaving: true,
      errorMessage: '',
      successMessage: ''
    });

    updateSuperAdminPermissionRequestStatus(item.id, {
      status: 'ACCEPTED',
      ...(isSportAcceptPreset(item) ? { sportIds: this.state.selectedSportIds } : {})
    })
      .then(updatedItem => {
        this.setState(
          {
            item: updatedItem,
            isSaving: false,
            successMessage: 'Permission request has been accepted.'
          },
          () => this.loadExistingRequesterPermissions(updatedItem)
        );
      })
      .catch(error => {
        console.error('Can not accept permission request', error);
        this.setState({
          isSaving: false,
          errorMessage: 'Can not accept permission request. Please try again.'
        });
      });
  };

  onAcceptStudentAsNewClick = () => {
    const { item, selectedFormId, selectedHouseId } = this.state;

    if (!item || !this.canAcceptStudentAsNew()) {
      return;
    }

    const requesterName = getRequesterName(item) || 'this student request';
    const isConfirmed = window.confirm(`Do you really want to accept ${requesterName} as a new student?`);

    if (!isConfirmed) {
      return;
    }

    this.setState({
      isSaving: true,
      errorMessage: '',
      successMessage: ''
    });

    updateSuperAdminPermissionRequestStatus(item.id, {
      status: 'ACCEPTED',
      formId: selectedFormId || undefined,
      houseId: selectedHouseId || undefined
    })
      .then(updatedItem => {
        this.setState(
          {
            item: updatedItem,
            isSaving: false,
            successMessage: 'Permission request has been accepted.'
          },
          () => this.loadExistingRequesterPermissions(updatedItem)
        );
      })
      .catch(error => {
        console.error('Can not accept student permission request', error);
        this.setState({
          isSaving: false,
          errorMessage: 'Can not accept student permission request. Please try again.'
        });
      });
  };

  onMergeStudentClick = () => {
    const { item, selectedMergeStudentId } = this.state;

    if (!item || !this.canMergeStudent()) {
      return;
    }

    const schoolId = getSchoolId(item);
    const requesterName = getRequesterName(item) || 'this student request';
    const selectedStudent = this.getSelectedMergeStudent();
    const selectedStudentName = getStudentName(selectedStudent) || 'selected student';

    if (!schoolId) {
      this.setState({
        errorMessage: 'Can not merge student because school is not set on this request.'
      });
      return;
    }

    const isConfirmed = window.confirm(
      `Do you really want to merge ${requesterName} with existing student ${selectedStudentName}?`
    );

    if (!isConfirmed) {
      return;
    }

    this.setState({
      isSaving: true,
      errorMessage: '',
      successMessage: ''
    });

    mergeSuperAdminSchoolStudent(schoolId, selectedMergeStudentId, item.requesterId, item.id)
      .then(() => getSuperAdminPermissionRequest(item.id))
      .then(updatedItem => {
        this.setState(
          {
            item: updatedItem,
            selectedMergeStudentId: '',
            studentSearchText: '',
            isStudentDropdownOpen: false,
            isSaving: false,
            successMessage: 'Student request has been accepted and merged.'
          },
          () => this.loadExistingRequesterPermissions(updatedItem)
        );
      })
      .catch(error => {
        console.error('Can not merge student permission request', error);
        this.setState({
          isSaving: false,
          errorMessage: 'Can not merge student permission request. Please try again.'
        });
      });
  };

  onAcceptParentForSelectedStudentClick = () => {
    const { item, selectedParentStudentId } = this.state;

    if (!item || !this.canAcceptParentForSelectedStudent()) {
      return;
    }

    const requesterName = getRequesterName(item) || 'this parent request';
    const selectedStudent = this.getSelectedParentStudent();
    const selectedStudentName = getStudentName(selectedStudent) || 'selected student';
    const isConfirmed = window.confirm(
      `Do you really want to accept ${requesterName} as a parent of ${selectedStudentName}?`
    );

    if (!isConfirmed) {
      return;
    }

    this.setState({
      isSaving: true,
      errorMessage: '',
      successMessage: ''
    });

    updateSuperAdminPermissionRequestStatus(item.id, {
      status: 'ACCEPTED',
      studentId: selectedParentStudentId
    })
      .then(updatedItem => {
        this.setState(
          {
            item: updatedItem,
            selectedParentStudentId: '',
            parentStudentSearchText: '',
            isParentStudentDropdownOpen: false,
            isSaving: false,
            successMessage: 'Parent permission request has been accepted.'
          },
          () => this.loadExistingRequesterPermissions(updatedItem)
        );
      })
      .catch(error => {
        console.error('Can not accept parent permission request', error);
        this.setState({
          isSaving: false,
          errorMessage: 'Can not accept parent permission request. Please try again.'
        });
      });
  };

  onSportSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      sportSearchText: event.target.value,
      isSportDropdownOpen: true
    });
  };

  onSportSearchFocus = () => {
    this.setState({
      isSportDropdownOpen: true
    });
  };

  onSportSelectClick = (sportId: string) => {
    this.setState(prevState => {
      const isAlreadySelected = prevState.selectedSportIds.indexOf(sportId) !== -1;

      return {
        selectedSportIds: isAlreadySelected ? prevState.selectedSportIds : [...prevState.selectedSportIds, sportId],
        sportSearchText: '',
        isSportDropdownOpen: false
      };
    });
  };

  onSportRemoveClick = (sportId: string) => {
    this.setState(prevState => ({
      selectedSportIds: prevState.selectedSportIds.filter(id => id !== sportId)
    }));
  };

  onSportDropdownToggleClick = () => {
    this.setState(prevState => ({
      isSportDropdownOpen: !prevState.isSportDropdownOpen
    }));
  };

  onStudentFormChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    this.setState({
      selectedFormId: event.target.value
    });
  };

  onStudentHouseChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    this.setState({
      selectedHouseId: event.target.value
    });
  };

  onStudentSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      studentSearchText: event.target.value,
      isStudentDropdownOpen: true
    });
  };

  onStudentSearchFocus = () => {
    this.setState({
      isStudentDropdownOpen: true
    });
  };

  onStudentDropdownToggleClick = () => {
    this.setState(prevState => ({
      isStudentDropdownOpen: !prevState.isStudentDropdownOpen
    }));
  };

  onMergeStudentSelectClick = (studentId: string) => {
    const student = this.state.students.find(item => getStudentId(item) === studentId);

    this.setState({
      selectedMergeStudentId: studentId,
      studentSearchText: student ? getStudentName(student) : '',
      isStudentDropdownOpen: false
    });
  };

  onMergeStudentClearClick = () => {
    this.setState({
      selectedMergeStudentId: '',
      studentSearchText: '',
      isStudentDropdownOpen: false
    });
  };

  onParentFormChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    this.setState({
      selectedParentFormId: event.target.value,
      selectedParentStudentId: '',
      parentStudentSearchText: '',
      isParentStudentDropdownOpen: false
    });
  };

  onParentHouseChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    this.setState({
      selectedParentHouseId: event.target.value,
      selectedParentStudentId: '',
      parentStudentSearchText: '',
      isParentStudentDropdownOpen: false
    });
  };

  onParentStudentSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      parentStudentSearchText: event.target.value,
      isParentStudentDropdownOpen: true
    });
  };

  onParentStudentSearchFocus = () => {
    this.setState({
      isParentStudentDropdownOpen: true
    });
  };

  onParentStudentDropdownToggleClick = () => {
    this.setState(prevState => ({
      isParentStudentDropdownOpen: !prevState.isParentStudentDropdownOpen
    }));
  };

  onParentStudentSelectClick = (studentId: string) => {
    const student = this.state.students.find(item => getStudentId(item) === studentId);

    this.setState({
      selectedParentStudentId: studentId,
      parentStudentSearchText: student ? getStudentName(student) : '',
      isParentStudentDropdownOpen: false
    });
  };

  onParentStudentClearClick = () => {
    this.setState({
      selectedParentStudentId: '',
      parentStudentSearchText: '',
      isParentStudentDropdownOpen: false
    });
  };

  getSelectedSports(): SuperAdminSport[] {
    const { selectedSportIds, sports } = this.state;

    return selectedSportIds
      .map(sportId => sports.find(sport => sport.id === sportId))
      .filter(sport => typeof sport !== 'undefined') as SuperAdminSport[];
  }

  getFilteredSports(): SuperAdminSport[] {
    const { selectedSportIds, sportSearchText, sports } = this.state;
    const searchText = sportSearchText.trim().toLowerCase();

    return sports
      .filter(sport => selectedSportIds.indexOf(sport.id) === -1)
      .filter(sport => {
        if (searchText === '') {
          return true;
        }

        return (sport.name || '').toLowerCase().indexOf(searchText) !== -1;
      })
      .slice(0, 20);
  }

  getFilteredMergeStudents(): any[] {
    const { item, selectedMergeStudentId, students, studentSearchText } = this.state;
    const searchText = studentSearchText.trim().toLowerCase();
    const requesterId = item ? item.requesterId : '';

    return students
      .filter(student => getStudentId(student) !== requesterId)
      .filter(student => getStudentId(student) !== selectedMergeStudentId)
      .filter(student => {
        if (searchText === '') {
          return true;
        }

        return getStudentSearchText(student).indexOf(searchText) !== -1;
      })
      .slice(0, 20);
  }

  getSelectedMergeStudent(): any {
    const { selectedMergeStudentId, students } = this.state;

    return students.find(student => getStudentId(student) === selectedMergeStudentId);
  }

  getFilteredParentStudents(): any[] {
    const {
      item,
      parentStudentSearchText,
      selectedParentFormId,
      selectedParentHouseId,
      selectedParentStudentId,
      students
    } = this.state;
    const searchText = parentStudentSearchText.trim().toLowerCase();
    const requesterId = item ? item.requesterId : '';

    return students
      .filter(student => getStudentId(student) !== requesterId)
      .filter(student => getStudentId(student) !== selectedParentStudentId)
      .filter(student => selectedParentFormId === '' || getStudentFormId(student) === selectedParentFormId)
      .filter(student => selectedParentHouseId === '' || getStudentHouseId(student) === selectedParentHouseId)
      .filter(student => {
        if (searchText === '') {
          return true;
        }

        return getStudentSearchText(student).indexOf(searchText) !== -1;
      })
      .slice(0, 20);
  }

  getSelectedParentStudent(): any {
    const { selectedParentStudentId, students } = this.state;

    return students.find(student => getStudentId(student) === selectedParentStudentId);
  }

  getPermissionId(permission: any): string {
    return getEntityId(permission);
  }

  getPermissionRole(permission: any): string {
    return permission.preset || permission.role || propz.get(permission, ['permission', 'preset'], '');
  }

  getPermissionSchoolName(permission: any): string {
    return (
      propz.get(permission, ['school', 'name'], '') ||
      propz.get(permission, ['schoolData', 'name'], '') ||
      permission.schoolName ||
      permission.schoolId ||
      ''
    );
  }

  getPermissionStudentName(permission: any): string {
    const firstName =
      propz.get(permission, ['student', 'firstName'], '') ||
      propz.get(permission, ['child', 'firstName'], '') ||
      propz.get(permission, ['studentData', 'firstName'], '');
    const lastName =
      propz.get(permission, ['student', 'lastName'], '') ||
      propz.get(permission, ['child', 'lastName'], '') ||
      propz.get(permission, ['studentData', 'lastName'], '');
    const fullName = `${firstName} ${lastName}`.trim();

    return (
      fullName ||
      propz.get(permission, ['student', 'name'], '') ||
      propz.get(permission, ['child', 'name'], '') ||
      permission.studentId ||
      permission.childId ||
      ''
    );
  }

  getPermissionSportsText(permission: any): string {
    const sports = permission.sports || [];

    if (Array.isArray(sports) && sports.length > 0) {
      return sports
        .map(sport => sport.name)
        .filter(name => typeof name !== 'undefined' && name !== '')
        .join(', ');
    }

    return Array.isArray(permission.sportIds) && permission.sportIds.length > 0 ? permission.sportIds.join(', ') : '';
  }

  getPermissionStatus(permission: any): string {
    return permission.status || (permission.isRemoved ? 'REMOVED' : 'ACTIVE');
  }

  renderValue(label: string, value: any) {
    const text = stringifyValue(value);

    return (
      <div className="mb-3">
        <div className="text-muted small">{label}</div>
        <div style={{ whiteSpace: 'pre-wrap' }}>{text || '-'}</div>
      </div>
    );
  }

  renderBadge(status?: string) {
    const badgeClass =
      status === 'NEW'
        ? 'badge badge-primary'
        : status === 'ACCEPTED'
        ? 'badge badge-success'
        : status === 'REJECTED'
        ? 'badge badge-danger'
        : 'badge badge-secondary';

    return <span className={badgeClass}>{status || 'Unknown'}</span>;
  }

  renderStudentOptionButton(student: any, onClick: () => void) {
    return (
      <button
        key={getStudentId(student)}
        type="button"
        className="btn btn-link btn-block text-left px-3 py-2"
        style={{ textDecoration: 'none' }}
        onClick={onClick}
      >
        <div>{getStudentName(student)}</div>
        <div className="text-muted small">{getStudentDescription(student)}</div>
      </button>
    );
  }

  renderSelectedStudentBadge(student: any, onClearClick: () => void) {
    return (
      <span className="badge badge-primary mr-2 mb-2 p-2">
        {getStudentName(student)}
        {getStudentDescription(student) ? ` · ${getStudentDescription(student)}` : ''}
        <button
          type="button"
          className="btn btn-link btn-sm text-white ml-2 p-0"
          style={{ verticalAlign: 'baseline' }}
          onClick={onClearClick}
        >
          ×
        </button>
      </span>
    );
  }

  renderRequesterCard(item: SuperAdminPermissionRequestModel) {
    return (
      <div className="card mb-3">
        <div className="card-header d-flex justify-content-between align-items-center">
          <strong>Requester</strong>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={this.onOpenRequesterClick}
            disabled={!getRequesterId(item)}
          >
            Open user
          </button>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-4">{this.renderValue('Name', getRequesterName(item))}</div>
            <div className="col-md-4">{this.renderValue('Email', propz.get(item, ['requester', 'email'], ''))}</div>
            <div className="col-md-4">{this.renderValue('Phone', propz.get(item, ['requester', 'phone'], ''))}</div>
          </div>

          <div className="row">
            <div className="col-md-4">
              {this.renderValue('Birthday', formatDateTime(propz.get(item, ['requester', 'birthday'], '')))}
            </div>
            <div className="col-md-4">{this.renderValue('Gender', propz.get(item, ['requester', 'gender'], ''))}</div>
            <div className="col-md-4">{this.renderValue('Requester ID', item.requesterId)}</div>
          </div>
        </div>
      </div>
    );
  }

  renderSportsSelector(item: SuperAdminPermissionRequestModel) {
    const { isSportDropdownOpen, sportSearchText } = this.state;
    const selectedSports = this.getSelectedSports();
    const filteredSports = this.getFilteredSports();

    if (!isSportAcceptPreset(item) || item.status !== 'NEW') {
      return null;
    }

    return (
      <div className="card mb-3">
        <div className="card-header">
          <strong>Sports for {getRole(item)} permission</strong>
        </div>

        <div className="card-body">
          <div className="text-muted mb-2">
            Search and select sports to attach to this permission. Leave empty only if the user should have access
            without specific sports.
          </div>

          <div style={{ maxWidth: 520, position: 'relative' }}>
            <div className="d-flex">
              <input
                type="text"
                className="form-control"
                value={sportSearchText}
                onChange={this.onSportSearchChange}
                onFocus={this.onSportSearchFocus}
                placeholder="Search sport..."
              />
              <button
                type="button"
                className="btn btn-outline-secondary ml-2"
                onClick={this.onSportDropdownToggleClick}
              >
                {isSportDropdownOpen ? 'Close' : 'Show'}
              </button>
            </div>

            {isSportDropdownOpen && (
              <div
                className="border bg-white mt-1"
                style={{
                  position: 'absolute',
                  zIndex: 20,
                  width: '100%',
                  maxHeight: 260,
                  overflowY: 'auto',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
                }}
              >
                {filteredSports.length === 0 && <div className="p-2 text-muted">No sports found.</div>}

                {filteredSports.map(sport => (
                  <button
                    key={sport.id}
                    type="button"
                    className="btn btn-link btn-block text-left px-3 py-2"
                    style={{ textDecoration: 'none' }}
                    onClick={() => this.onSportSelectClick(sport.id)}
                  >
                    {sport.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {selectedSports.length > 0 && (
            <div className="mt-3">
              <div className="text-muted small mb-1">Selected sports</div>

              {selectedSports.map(sport => (
                <span key={sport.id} className="badge badge-primary mr-2 mb-2 p-2">
                  {sport.name}
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-white ml-2 p-0"
                    style={{ verticalAlign: 'baseline' }}
                    onClick={() => this.onSportRemoveClick(sport.id)}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  renderStudentAcceptSelector(item: SuperAdminPermissionRequestModel) {
    const {
      forms,
      houses,
      isSchoolDataLoading,
      isStudentDropdownOpen,
      selectedFormId,
      selectedHouseId,
      selectedMergeStudentId,
      studentSearchText
    } = this.state;
    const filteredStudents = this.getFilteredMergeStudents();
    const selectedMergeStudent = this.getSelectedMergeStudent();

    if (!isStudentAcceptPreset(item) || item.status !== 'NEW') {
      return null;
    }

    return (
      <div className="card mb-3">
        <div className="card-header">
          <strong>Student accept options</strong>
        </div>

        <div className="card-body">
          {isSchoolDataLoading && <div className="text-muted mb-2">Loading forms, houses and students...</div>}

          <h5>Accept as new student</h5>
          <div className="text-muted mb-2">
            Use this when the requester should become a student account without merging into an existing student record.
          </div>

          <div className="row">
            <div className="col-md-4">
              <div className="form-group">
                <label>Form</label>
                <select className="form-control" value={selectedFormId} onChange={this.onStudentFormChange}>
                  <option value="">Not set</option>
                  {forms.map(form => (
                    <option key={form.id} value={form.id}>
                      {form.name}
                      {typeof form.age !== 'undefined' ? `, age ${form.age}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="col-md-4">
              <div className="form-group">
                <label>House</label>
                <select className="form-control" value={selectedHouseId} onChange={this.onStudentHouseChange}>
                  <option value="">Not set</option>
                  {houses.map(house => (
                    <option key={house.id} value={house.id}>
                      {house.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <button
            type="button"
            className="btn btn-success"
            onClick={this.onAcceptStudentAsNewClick}
            disabled={this.state.isSaving || !this.canAcceptStudentAsNew()}
          >
            Accept as new student
          </button>

          <hr />

          <h5>Accept and merge</h5>
          <div className="text-muted mb-2">
            Use this when the student already exists in the school and this request should be merged into that existing
            student record.
          </div>

          <div style={{ maxWidth: 720, position: 'relative' }}>
            <div className="d-flex">
              <input
                type="text"
                className="form-control"
                value={studentSearchText}
                onChange={this.onStudentSearchChange}
                onFocus={this.onStudentSearchFocus}
                placeholder="Search existing student by name, email, form, house or MIS ID..."
              />
              <button
                type="button"
                className="btn btn-outline-secondary ml-2"
                onClick={this.onStudentDropdownToggleClick}
              >
                {isStudentDropdownOpen ? 'Close' : 'Show'}
              </button>
            </div>

            {isStudentDropdownOpen && (
              <div
                className="border bg-white mt-1"
                style={{
                  position: 'absolute',
                  zIndex: 20,
                  width: '100%',
                  maxHeight: 320,
                  overflowY: 'auto',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
                }}
              >
                {filteredStudents.length === 0 && <div className="p-2 text-muted">No students found.</div>}

                {filteredStudents.map(student =>
                  this.renderStudentOptionButton(student, () => this.onMergeStudentSelectClick(getStudentId(student)))
                )}
              </div>
            )}
          </div>

          {selectedMergeStudentId && selectedMergeStudent && (
            <div className="mt-3">
              <div className="text-muted small mb-1">Selected existing student</div>
              {this.renderSelectedStudentBadge(selectedMergeStudent, this.onMergeStudentClearClick)}
            </div>
          )}

          <button
            type="button"
            className="btn btn-success mt-2"
            onClick={this.onMergeStudentClick}
            disabled={this.state.isSaving || !this.canMergeStudent()}
          >
            Accept and merge
          </button>
        </div>
      </div>
    );
  }

  renderParentAcceptSelector(item: SuperAdminPermissionRequestModel) {
    const {
      forms,
      houses,
      isParentStudentDropdownOpen,
      isSchoolDataLoading,
      parentStudentSearchText,
      selectedParentFormId,
      selectedParentHouseId,
      selectedParentStudentId
    } = this.state;
    const filteredStudents = this.getFilteredParentStudents();
    const selectedParentStudent = this.getSelectedParentStudent();

    if (!isParentAcceptPreset(item) || item.status !== 'NEW') {
      return null;
    }

    return (
      <div className="card mb-3">
        <div className="card-header">
          <strong>Parent accept options</strong>
        </div>

        <div className="card-body">
          {isSchoolDataLoading && <div className="text-muted mb-2">Loading forms, houses and students...</div>}

          <div className="text-muted mb-2">
            Filter by form and house first if useful, then search/select the child. Student details are shown to avoid
            linking the wrong child.
          </div>

          <div className="row">
            <div className="col-md-4">
              <div className="form-group">
                <label>Form filter</label>
                <select className="form-control" value={selectedParentFormId} onChange={this.onParentFormChange}>
                  <option value="">All forms</option>
                  {forms.map(form => (
                    <option key={form.id} value={form.id}>
                      {form.name}
                      {typeof form.age !== 'undefined' ? `, age ${form.age}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="col-md-4">
              <div className="form-group">
                <label>House filter</label>
                <select className="form-control" value={selectedParentHouseId} onChange={this.onParentHouseChange}>
                  <option value="">All houses</option>
                  {houses.map(house => (
                    <option key={house.id} value={house.id}>
                      {house.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div style={{ maxWidth: 760, position: 'relative' }}>
            <div className="d-flex">
              <input
                type="text"
                className="form-control"
                value={parentStudentSearchText}
                onChange={this.onParentStudentSearchChange}
                onFocus={this.onParentStudentSearchFocus}
                placeholder="Search child by name, surname, email, form, house, birthday or MIS ID..."
              />
              <button
                type="button"
                className="btn btn-outline-secondary ml-2"
                onClick={this.onParentStudentDropdownToggleClick}
              >
                {isParentStudentDropdownOpen ? 'Close' : 'Show'}
              </button>
            </div>

            {isParentStudentDropdownOpen && (
              <div
                className="border bg-white mt-1"
                style={{
                  position: 'absolute',
                  zIndex: 20,
                  width: '100%',
                  maxHeight: 340,
                  overflowY: 'auto',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
                }}
              >
                {filteredStudents.length === 0 && <div className="p-2 text-muted">No students found.</div>}

                {filteredStudents.map(student =>
                  this.renderStudentOptionButton(student, () => this.onParentStudentSelectClick(getStudentId(student)))
                )}
              </div>
            )}
          </div>

          {selectedParentStudentId && selectedParentStudent && (
            <div className="mt-3">
              <div className="text-muted small mb-1">Selected child</div>
              {this.renderSelectedStudentBadge(selectedParentStudent, this.onParentStudentClearClick)}
            </div>
          )}

          <button
            type="button"
            className="btn btn-success mt-2"
            onClick={this.onAcceptParentForSelectedStudentClick}
            disabled={this.state.isSaving || !this.canAcceptParentForSelectedStudent()}
          >
            Accept parent for selected child
          </button>
        </div>
      </div>
    );
  }

  renderExistingPermissionsCard() {
    const { existingPermissions, isExistingPermissionsLoading, item } = this.state;

    if (!item) {
      return null;
    }

    return (
      <div className="card mb-3">
        <div className="card-header d-flex justify-content-between align-items-center">
          <strong>Existing requester permissions</strong>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={this.onOpenRequesterClick}
            disabled={!getRequesterId(item)}
          >
            Open user
          </button>
        </div>

        <div className="card-body">
          {isExistingPermissionsLoading && <div className="text-muted">Loading existing permissions...</div>}

          {!isExistingPermissionsLoading && existingPermissions.length === 0 && (
            <div className="text-muted">No existing permissions found for this requester.</div>
          )}

          {!isExistingPermissionsLoading && existingPermissions.length > 0 && (
            <div className="table-responsive">
              <table className="table table-sm table-bordered mb-0">
                <thead>
                  <tr>
                    <th>Role</th>
                    <th>School</th>
                    <th>Student / child</th>
                    <th>Sports</th>
                    <th>Status</th>
                    <th>Activated</th>
                    <th>Deactivated</th>
                  </tr>
                </thead>
                <tbody>
                  {existingPermissions.map((permission: any, index: number) => (
                    <tr key={this.getPermissionId(permission) || `permission-${index}`}>
                      <td>{this.getPermissionRole(permission) || '-'}</td>
                      <td>{this.getPermissionSchoolName(permission) || '-'}</td>
                      <td>{this.getPermissionStudentName(permission) || '-'}</td>
                      <td>{this.getPermissionSportsText(permission) || '-'}</td>
                      <td>{this.getPermissionStatus(permission) || '-'}</td>
                      <td>{formatDateTime(permission.activatedAt) || '-'}</td>
                      <td>{formatDateTime(permission.deactivatedAt) || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  renderPermissionCard(item: SuperAdminPermissionRequestModel) {
    return (
      <div className="card mb-3">
        <div className="card-header d-flex justify-content-between align-items-center">
          <strong>Requested permission</strong>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={this.onOpenSchoolClick}
            disabled={!getSchoolId(item)}
          >
            Open school
          </button>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-4">{this.renderValue('Role', getRole(item))}</div>
            <div className="col-md-4">{this.renderValue('School', getSchoolName(item))}</div>
            <div className="col-md-4">
              {this.renderValue('School ID', propz.get(item, ['requestedPermission', 'schoolId'], ''))}
            </div>
          </div>

          <div className="row">
            <div className="col-md-4">{this.renderValue('Sports', getSportsText(item))}</div>
            <div className="col-md-4">{this.renderValue('Promo', item.promo)}</div>
            <div className="col-md-4">{this.renderValue('Optional permission ID', item.optPermissionId)}</div>
          </div>

          <div className="row">
            <div className="col-md-6">
              {this.renderValue('Comment', propz.get(item, ['requestedPermission', 'comment'], ''))}
            </div>
            <div className="col-md-6">
              {this.renderValue('Tournament', propz.get(item, ['tournament', 'name'], ''))}
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              {this.renderValue('Details', propz.get(item, ['requestedPermission', 'details'], ''))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderStatusCard(item: SuperAdminPermissionRequestModel) {
    return (
      <div className="card mb-3">
        <div className="card-header">
          <strong>Status</strong>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-3">
              <div className="text-muted small">Status</div>
              <div>{this.renderBadge(item.status)}</div>
            </div>
            <div className="col-md-3">{this.renderValue('Created at', formatDateTime(item.createdAt))}</div>
            <div className="col-md-3">{this.renderValue('Updated at', formatDateTime(item.updatedAt))}</div>
            <div className="col-md-3">{this.renderValue('Status set at', formatDateTime(item.statusSetAt))}</div>
          </div>

          <div className="row">
            <div className="col-md-12">{this.renderValue('Status set by', item.statusSetBy)}</div>
          </div>
        </div>
      </div>
    );
  }

  renderRawCard(item: SuperAdminPermissionRequestModel) {
    return (
      <details className="mb-3">
        <summary>Raw permission request data</summary>
        <pre className="mt-2 p-3 bg-light border" style={{ whiteSpace: 'pre-wrap' }}>
          {JSON.stringify(item, null, 2)}
        </pre>
      </details>
    );
  }

  render() {
    const { errorMessage, item, isLoading, isSaving, successMessage } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    if (!item) {
      return (
        <div className="container-fluid py-4">
          {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
          <button className="btn btn-outline-secondary" onClick={this.onBackClick}>
            Back to permission requests
          </button>
        </div>
      );
    }

    return (
      <div className="container-fluid py-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h2 className="mb-1">Permission request</h2>
            <div className="text-muted">
              {getRequesterName(item) || item.id} · {getRole(item)}
            </div>
          </div>

          <div>
            <button className="btn btn-outline-secondary mr-2" onClick={this.onBackClick} disabled={isSaving}>
              Back
            </button>
            <button className="btn btn-outline-secondary mr-2" onClick={this.loadRequest} disabled={isSaving}>
              Refresh
            </button>
            <button
              className="btn btn-success mr-2"
              onClick={this.onAcceptClick}
              disabled={isSaving || !this.canAccept()}
            >
              Accept request
            </button>
            <button className="btn btn-danger" onClick={this.onRejectClick} disabled={isSaving || !this.canReject()}>
              Reject request
            </button>
          </div>
        </div>

        {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
        {successMessage && <div className="alert alert-success">{successMessage}</div>}

        {item.status === 'NEW' &&
          !isSimpleAcceptPreset(item) &&
          !isSportAcceptPreset(item) &&
          !isStudentAcceptPreset(item) &&
          !isParentAcceptPreset(item) && (
            <div className="alert alert-info">
              Accept flow for {getRole(item)} requests requires additional details and will be added separately.
            </div>
          )}

        {this.renderStatusCard(item)}
        {this.renderRequesterCard(item)}
        {this.renderExistingPermissionsCard()}
        {this.renderPermissionCard(item)}
        {this.renderSportsSelector(item)}
        {this.renderStudentAcceptSelector(item)}
        {this.renderParentAcceptSelector(item)}
        {this.renderRawCard(item)}
      </div>
    );
  }
}

